import { NoIndex } from "@/components/layout/Seo";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BadgeCheck, ShieldCheck, Sparkles } from "lucide-react";
import { GlowCard, Logo } from "@/components/ui";
import { usePageTransition } from "@/motion/usePageTransition";

const POINTS = [
  { icon: ShieldCheck, title: "RealitySafe escrow", body: "Pay once; vendors get paid only after you confirm delivery." },
  { icon: BadgeCheck, title: "Verified vendors", body: "Every paying vendor passes BVN/NIN and bank-name checks." },
  { icon: Sparkles, title: "0% commission", body: "Vendors keep every naira of the sale on a flat monthly plan." },
];

/** Split-screen auth shell: aurora brand panel + glowing form card. */
export function AuthLayout() {
  const { pathname } = useLocation();
  const ref = usePageTransition<HTMLDivElement>(pathname);
  return (
    <div className="grain relative grid min-h-dvh overflow-hidden lg:grid-cols-[1.1fr_1fr]">
      <NoIndex />
      <div className="aurora" aria-hidden />
      <section
        className="relative hidden flex-col justify-between p-12 pt-[calc(48px+var(--safe-top))] lg:flex"
        aria-label="Why RealityStore"
      >
        <Link to="/" aria-label="RealityStore home" className="w-fit rounded-lg">
          <Logo />
        </Link>
        <div className="max-w-lg">
          <h1 className="text-5xl font-bold leading-[1.05]">
            Where we bring all your <span className="text-gradient">dreams to reality.</span>
          </h1>
          <ul className="mt-10 flex flex-col gap-5">
            {POINTS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-[var(--border-glow)] bg-surface text-cyan shadow-glow-1">
                  <Icon className="size-5" aria-hidden />
                </span>
                <span>
                  <span className="block font-medium text-ink">{title}</span>
                  <span className="text-sm text-ink-muted">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-ink-muted">Nigeria-only marketplace · Prices in ₦</p>
      </section>
      <main
        id="main"
        className="relative flex flex-col items-center justify-start px-4 pb-[calc(24px+var(--safe-bottom))] pt-[calc(20px+var(--safe-top))] sm:justify-center sm:px-8 sm:py-10"
      >
        <Link to="/" aria-label="RealityStore home" className="mb-6 rounded-lg sm:mb-8 lg:hidden">
          <Logo />
        </Link>
        <div ref={ref} className="w-full max-w-md">
          <GlowCard elevation={2} className="p-5 xs:p-6 sm:p-8">
            <Outlet />
          </GlowCard>
        </div>
      </main>
    </div>
  );
}
